import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, GitPullRequest } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function CategoryRequests() {
  const { user } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [tab, setTab] = useState("pending");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const isAdmin = user?.role === "admin";

  const load = () => api.get(`/category-requests?status=${tab}`).then((r) => setReqs(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [tab]);

  const approve = async (id) => {
    try {
      await api.post(`/category-requests/${id}/approve`);
      toast.success("Category approved and created — linked listing is now live");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reject = async (id) => {
    try {
      await api.post(`/category-requests/${id}/reject`, { action: "reject", reason });
      toast.success("Request rejected");
      setRejecting(null);
      setReason("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="category-requests-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#1B2A4A]">Category Requests</h1>
        <p className="text-sm text-[#475569] mt-1">
          Employee-proposed categories. Approval creates the category and publishes the linked listing.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E2E8F0]">
          <TabsTrigger value="pending" data-testid="tab-pending-requests">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved-requests">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected-requests">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-requests">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-3" data-testid="requests-list">
        {reqs.length === 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center text-[#94A3B8]" data-testid="requests-empty">
            No {tab === "all" ? "" : tab} requests.
          </div>
        )}
        {reqs.map((r) => (
          <div key={r.id} data-testid={`request-row-${r.id}`} className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-xl bg-[#F5961B]/10 flex items-center justify-center shrink-0">
                <GitPullRequest size={20} className="text-[#F5961B]" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="font-semibold text-[#0F172A] text-base">{r.name}</div>
                <div className="text-sm text-[#475569] mt-0.5">
                  {r.parent_name ? <>Subcategory of <strong>{r.parent_name}</strong> · </> : "Top-level category · "}
                  requested by <strong>{r.requested_by_name}</strong> · {new Date(r.created_at).toLocaleString()}
                </div>
                {r.reason && <div className="text-sm text-[#475569] mt-1.5 bg-[#F5F7FA] rounded-lg px-3 py-2">"{r.reason}"</div>}
                {r.listing_name && (
                  <Link to={`/admin/listings/${r.listing_id}`} data-testid={`request-listing-link-${r.id}`} className="text-sm font-medium text-[#2E7BD6] hover:underline mt-1.5 inline-block">
                    Linked listing: {r.listing_name}
                  </Link>
                )}
                {r.status !== "pending" && (
                  <div className="text-xs text-[#94A3B8] mt-1.5">
                    {r.status === "approved" ? "Approved" : "Rejected"} by {r.reviewed_by}
                    {r.rejection_reason && ` — ${r.rejection_reason}`}
                  </div>
                )}
              </div>
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
                r.status === "approved" ? "text-[#047857] bg-[#D1FAE5] border-[#6EE7B7]" :
                r.status === "rejected" ? "text-[#B91C1C] bg-[#FEE2E2] border-[#FECACA]" :
                "text-[#B45309] bg-[#FEF3C7] border-[#FDE68A]"
              }`} data-testid={`request-status-${r.id}`}>{r.status}</span>
              {r.status === "pending" && isAdmin && (
                <div className="flex gap-2">
                  <Button data-testid={`approve-request-${r.id}`} onClick={() => approve(r.id)} className="bg-[#059669] hover:bg-[#047857] text-white">
                    <Check size={15} className="mr-1.5" /> Approve
                  </Button>
                  <Button data-testid={`reject-request-${r.id}`} variant="outline" onClick={() => setRejecting(rejecting === r.id ? null : r.id)} className="border-[#DC2626] text-[#DC2626] hover:bg-red-50">
                    <X size={15} className="mr-1.5" /> Reject
                  </Button>
                </div>
              )}
            </div>
            {rejecting === r.id && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-[#F1F5F9]">
                <Input data-testid={`reject-request-reason-${r.id}`} placeholder="Rejection reason..." value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1" />
                <Button data-testid={`confirm-reject-request-${r.id}`} onClick={() => reject(r.id)} className="bg-[#DC2626] hover:bg-[#b91c1c] text-white">Confirm Reject</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
